import {
    queryGraphQL,
    mutateGraphQL
} from 'common/util/query';
import {
    toQuery,
    toMutation,
    nodeWithArgs,
    node,
    enumeration,
} from 'common/util/queryHelper';
import lodash from 'lodash';
import { replace } from 'react-router-redux';
import { addApplicationMessages } from 'common/actions/applicationMessagesActions';
import { buildQueryForSave } from 'complionAdmin/actions/binderTemplate';
import Query from '@complion/query';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { updateMe } from 'common/actions/login';
const { jsonHelper } = Query;

export const LOAD_BINDER_USERS = 'LOAD_BINDER_USERS';
export const LOAD_BINDER = 'LOAD_BINDER';
export const LOADING_BINDER = 'LOADING_BINDER';
export const FAILED_LOADING_BINDER = 'FAILED_LOADING_BINDER';
export const REMOVE_BINDER = 'REMOVE_BINDER';
export const REMOVE_USERS = 'REMOVE_USERS';
export const LOAD_DOCUMENT = 'LOAD_DOCUMENT';
export const BINDER_CONFIG_ERRORS = 'BINDER_CONFIG_ERRORS';
export const DECREMENT_COUNT_UNFILED_DOCUMENT = 'DECREMENT_COUNT_UNFILED_DOCUMENT';
export const LOADING_BINDER_COUNT_UNFILED_DOCUMENTS = 'LOADING_BINDER_COUNT_UNFILED_DOCUMENTS';
export const LOAD_BINDER_STATUS_OPTIONS = 'LOAD_BINDER_STATUS_OPTIONS';

export function saveBinder(status = 'NEW') {
    return (dispatch, getState) => {
        const storeState = getState();
        const binder = lodash.cloneDeep(storeState.binder);

        binder.status = status;
        const folders = lodash.get(binder, 'layout.folders', []);
        const rootFolderIndex = lodash.findIndex(folders, folder => folder.parentId === null);

        if (rootFolderIndex === -1) {
            throw new Error('Could not find rootFolderIndex');
        }

        const rootFolder = folders[rootFolderIndex];
        return buildQueryForSave(storeState, rootFolder.id).then(builtQuery => {
            // remark: this will also clear errors if no errors are longer present
            dispatch(binderConfigErrors(binder.id, builtQuery.validationErrors.errors));
            // remark: this mutation is meaningful; it is part of the layout object that is placed in the updatedBinder.
            // it is a correction that makes sure the folders serialize correctly. A better implementation is needed.
            rootFolder.query = JSON.parse(jsonHelper.stringify(builtQuery.query));
            const updatedBinder = lodash.pick(binder, ['title', 'description', 'layout', 'status']);
            updatedBinder.layout = JSON.stringify(updatedBinder.layout);

            const mutation = nodeWithArgs('saveBinder', {
                id: binder.id,
                binder: updatedBinder
            });

            return queryGraphQL(toMutation(mutation)).then(response => {
                if (response.errors) {
                    dispatch(addApplicationMessages('error', lodash.map(response.errors, 'message')));
                    return;
                }
                dispatch(addApplicationMessage('success', 'Binder Settings update succeeded'));
                dispatch(loadBinder(binder.id));
            });
        });
    };
}

function binderConfigErrors(binderId, errors = []) {
    return {
        type: BINDER_CONFIG_ERRORS,
        payload: { binderId, errors }
    };
}

export function submitSaveBinderProperties(binder) {
    return (dispatch, getState) => {
        const renewalDate = lodash.isNil(binder.renewalDate) ? null : binder.renewalDate.toString();
        const eventsTabEnabled = binder.eventsTabEnabled ? true : false;
        const binderValues = lodash.pick(binder, [
            'title',
            'description',
            'trialType',
            'primaryInvestigator',
            'irbNumber',
            'contact',
            'trialId',
            'departmentSiteDg',
            'ctGovNumber',
            'sponsorCro',
            'status',
            'partnerMetadata',
        ]);

        const partnerMetadata = JSON.stringify(lodash.get(binder, ['partnerMetadata']));

        const mutation = toMutation([
            nodeWithArgs('updateBinderDetails', {
                id: binder.id,
                binder: { ...binderValues, renewalDate, eventsTabEnabled, partnerMetadata }
            }, [
                node('binder', [
                    'title',
                    'description',
                    'trialType',
                    'primaryInvestigator',
                    'irbNumber',
                    'contact',
                    'trialId',
                    'departmentSiteDg',
                    'ctGovNumber',
                    'sponsorCro',
                    'renewalDate',
                    'status',
                    node('partnerMetadata', [
                        'connexusStudyId',
                        'connexusSiteId',
                        'velosStudyId',
                    ]),
                ])
            ])
        ]);

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            } else {
                const currentBinder = lodash.get(getState(), 'binder');
                const updatedBinder = { ...currentBinder, ...binderValues, renewalDate, eventsTabEnabled };
                updatedBinder.layout = JSON.stringify(updatedBinder.layout);

                dispatch(loadBinderAction({ binder: updatedBinder }));
                dispatch(replace(`/binder/${binder.id}`));
            }
        });
    };
}

export function decrementUnfiledDocuments() {
    return (dispatch, getState) => {
        dispatch({
            type: DECREMENT_COUNT_UNFILED_DOCUMENT
        });
        const storeState = getState();
        const binderId = lodash.get(storeState, 'binder.id');
        return dispatch(countUnfiledDocuments(binderId));
    };
}

export function loadBinderUsers(binderId, institutionId) {
    return dispatch => {
        const query = toQuery([
            nodeWithArgs('binder', {
                id: binderId,
                institutionId: institutionId
            }, [
                nodeWithArgs('users', { isActive: true }, [
                    'id',
                    'sub',
                    'displayName',
                    'institutionId',
                    'firstName',
                    'lastName',
                ]),
            ])
        ]);

        return queryGraphQL(query).then(results => {
            dispatch({
                type: LOAD_BINDER_USERS,
                payload: results.data.binder.users,
            });
        });
    };
}

export function loadBinder(binderId, useSlimLayout) {
    return (dispatch, getState) => {
        dispatch(loadingBinder());

        const query = toQuery([
            nodeWithArgs('binder', {
                id: binderId
            }, [
                'id',
                'title',
                'description',
                'billable',
                nodeWithArgs('layout',{
                    useSlimLayout
                }),
                'status',
                'createdAt',
                'updatedAt',
                'trialType',
                'primaryInvestigator',
                'irbNumber',
                'trialId',
                'departmentSiteDg',
                'sponsorCro',
                'ctGovNumber',
                'contact',
                'renewalDate',
                'institutionTimezone',
                'binderTemplateId',
                'hasBinderValidationTemplateEnabled',
                'eventsTabEnabled',
                nodeWithArgs('users', { isActive: true }, [
                    'id',
                    'sub',
                    'displayName',
                    'institutionId',
                    'firstName',
                    'lastName',
                    nodeWithArgs('binderExpiration', {
                        binderId
                    }, [
                        'startDate',
                        'endDate',
                    ]),
                ]),
                node('createdBy', [
                    'displayName',
                    'emailAddress'
                ]),
                node('integrations', [
                    'name',
                    'enabled'
                ]),
                node('partnerMetadata', [
                    'connexusStudyId',
                    'connexusSiteId',
                    'connexusSubscribed',
                    'velosStudyId',
                ]),
            ]),
            node('me', [
                nodeWithArgs('binderPermissions', { binderId: binderId })
            ]),
        ]);

        const mainPageQueryRequest = queryGraphQL(query).then(results => {
            if (results.formattedErrors) {
                dispatch(failedLoadingBinder());
                return dispatch(addApplicationMessage('error', `Failed to load binder: ${lodash.head(results.formattedErrors)}`));
            } else {
                dispatch(updateMe({ binderPermissions: JSON.parse(results.data.me.binderPermissions) }));
                return dispatch(loadBinderAction(results.data, useSlimLayout));
            }
        });

        const countUnfiledDocumentsQueryRequest = dispatch(countUnfiledDocuments(binderId));

        return Promise.all([
            mainPageQueryRequest,
            countUnfiledDocumentsQueryRequest
        ]);
    };
}

export function removeBinder() {
    return {
        type: REMOVE_BINDER,
    };
}

export function removeUsers() {
    return {
        type: REMOVE_USERS,
    };
}

export function countUnfiledDocuments(binderId) {
    return dispatch => {
        if (!binderId) return Promise.resolve(null);
        const countUnfiledDocumentsQuery = toQuery(nodeWithArgs('countUnfiledDocuments', {
            query: {
                id: binderId
            }
        }));

        return queryGraphQL(countUnfiledDocumentsQuery).then(results => {
            if (results.errors) {
                return null; // no need to handle errors here because
            }
            dispatch(setCountUnfiledDocuments(results.data.countUnfiledDocuments));
        });
    };
}

function setCountUnfiledDocuments(countUnfiledDocuments) {
    return {
        type: LOADING_BINDER_COUNT_UNFILED_DOCUMENTS,
        payload: countUnfiledDocuments
    };
}

export function loadingBinder() {
    return {
        type: LOADING_BINDER
    };
}

export function failedLoadingBinder() {
    return {
        type: FAILED_LOADING_BINDER
    };
}

export function loadBinderAction({ binder, dataSources }, isBinderWithSlimLayout) {
    return {
        type: LOAD_BINDER,
        payload: {
            binder,
            dataSources,
            isBinderWithSlimLayout
        }
    };
}

export function loadDocumentAction(document) {
    return {
        type: LOAD_DOCUMENT,
        payload: document,
    };
}

export function loadDocument(documentId) {
    const query = toQuery(nodeWithArgs('document', {
        id: documentId
    }, [
        'id',
        'name',
        'documentStatus',
        'conversionStatus',
        'uploadDate',
        'binderId',
        node('uploadUser', [
            'displayName',
            'emailAddress',
        ]),
        node('documentType', [
            'id',
            'displayName'
        ]),
        node('descriptors', [
            'id',
            'displayName',
            'values'
        ]),
        nodeWithArgs('renditions', {
            type: enumeration('VIEW'),
        }, [
            'objectName',
            'containerId',
            'prettyFileName',
            'fileExt',
            'mimeType',
            'fileSize',
            'filePurpose',
            'createdDate',
            'type',
            node('downloadRequest', [
                'tempUrl'
            ])
        ])
    ]));

    return dispatch => queryGraphQL(query).then(results => dispatch(loadDocumentAction(results.data)));
}

export function loadBinderStatusOptions() {
    const query = toQuery([
        node('binderStatusOptions', [
            'id',
            'displayName',
        ]),
    ]);

    return dispatch => queryGraphQL(query).then(response => {
        if (response.errors) {
            dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
        } else {
            dispatch(loadBinderStatusOptionsAction(response.data.binderStatusOptions));
        }
    });
}


function loadBinderStatusOptionsAction(payload) {
    return {
        type: LOAD_BINDER_STATUS_OPTIONS,
        payload,
    };
}
